'use client';

import React, { memo, PropsWithChildren, useMemo } from 'react';
import {
  Label,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from 'recharts';

import { ChartConfig, ChartContainer } from '@repo/ui/components/ui/chart';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components/ui/tooltip';

import { useResponsiveRadialChartDimensions } from '@/lib/chart-utils';

// Constants
const credibilityChartConfig = {
  score: {
    label: 'Score',
  },
  notCredible: {
    label: 'Not Credible',
    color: 'hsl(var(--neutral-4))',
  },
  credible: {
    label: 'Credible',
    color: 'hsl(var(--facts-pie-chart))',
  },
} satisfies ChartConfig;

const biasChartConfig = {
  score: {
    label: 'Score',
  },
  notBias: {
    label: 'Not Bias',
    color: 'hsl(var(--neutral-4))',
  },
  bias: {
    label: 'Bias',
    color: 'hsl(var(--facts-pie-chart))',
  },
} satisfies ChartConfig;

// Components
interface TooltipTextProps {
  title: string;
  content?: string;
}

const PureTooltipText: React.FC<TooltipTextProps> = ({ title, content }) => (
  <div className="flex flex-col">
    <p className="text-[10px] font-bold leading-[13px] text-neutral-2 cred-md:text-[12px] cred-md:leading-[16px] cred-xl:text-[14px] cred-xl:leading-[18px]">
      {title}:
    </p>
    <p className="text-[10px] font-normal leading-[13px] text-neutral-2 cred-md:text-[12px] cred-md:leading-[16px] cred-xl:text-[14px] cred-xl:leading-[18px]">
      {content}
    </p>
  </div>
);
const TooltipText = memo(PureTooltipText, (prevProps, nextProps) => {
  return (
    prevProps.title === nextProps.title &&
    prevProps.content === nextProps.content
  );
});

interface ChartWrapperProps extends PropsWithChildren {
  title: string;
  content?: string;
}

const PureChartWrapper: React.FC<ChartWrapperProps> = ({
  children,
  title,
  content,
}) => (
  <TooltipProvider>
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="max-w-72 rounded-md bg-light-blue px-3 py-2 shadow-[1px_2px_4px_rgba(30,30,30,0.4)] cred-md:max-w-80 cred-xl:max-w-[440px]">
        <TooltipText title={title} content={content} />
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);
const ChartWrapper = memo(PureChartWrapper);

interface ArticleCardCrediblityChartProps {
  credibilityScore?: number;
  credibilityReasoning?: string;
}

const PureArticleCardCrediblityChart: React.FC<
  ArticleCardCrediblityChartProps
> = ({ credibilityScore, credibilityReasoning }) => {
  const chartDimensions = useResponsiveRadialChartDimensions('small');

  const credibilityChartData = useMemo(
    () => [
      {
        type: 'credible',
        score: Math.round(credibilityScore || 0),
        fill: 'hsl(var(--facts-pie-chart))',
      },
    ],
    [credibilityScore],
  );

  const credibilityEndAngle = useMemo(
    () =>
      credibilityChartData[0]?.score
        ? 90 + credibilityChartData[0]?.score * 3.6
        : 90,
    [credibilityChartData],
  );

  return (
    <ChartWrapper title="Reason" content={credibilityReasoning}>
      <ChartContainer
        config={credibilityChartConfig}
        className="aspect-square shrink-0 self-center size-[54px] cred-md:size-[63px] cred-lg:size-[68px] cred-xl:size-[90px]"
      >
        <RadialBarChart
          data={credibilityChartData}
          startAngle={90}
          endAngle={credibilityEndAngle}
          innerRadius={chartDimensions.innerRadius}
          outerRadius={chartDimensions.outerRadius}
          margin={{
            top: -10,
            right: -10,
            bottom: -10,
            left: -10,
          }}
        >
          <PolarGrid
            gridType="circle"
            radialLines={false}
            stroke="none"
            className="transition-colors duration-200 first:fill-[#CDD9FF] last:fill-neutral-1"
            polarRadius={chartDimensions.polarRadius}
          />
          <RadialBar dataKey="score" background cornerRadius={10} />
          <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
            <Label
              content={({ viewBox }) => {
                if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={viewBox.cy || 0}
                        className="fill-neutral-2 text-base font-bold leading-[19px] cred-md:text-lg cred-md:leading-[21px] cred-lg:text-xl cred-lg:leading-[23px] cred-xl:text-[26px] cred-xl:leading-[30px]"
                      >
                        {credibilityChartData[0]?.score + '%'}
                      </tspan>
                    </text>
                  );
                }
              }}
            />
          </PolarRadiusAxis>
        </RadialBarChart>
      </ChartContainer>
    </ChartWrapper>
  );
};
export const ArticleCardCrediblityChart = memo(
  PureArticleCardCrediblityChart,
  (prevProps, nextProps) => {
    return (
      prevProps.credibilityScore === nextProps.credibilityScore &&
      prevProps.credibilityReasoning === nextProps.credibilityReasoning
    );
  },
);

interface ArticleCardBiasChartProps {
  biasScore?: number;
  biasLabel?: string;
  biasLabelReasoning?: string;
}

const PureArticleCardBiasChart: React.FC<ArticleCardBiasChartProps> = ({
  biasScore,
  biasLabel,
  biasLabelReasoning,
}) => {
  const chartDimensions = useResponsiveRadialChartDimensions('small');

  const biasChartData = useMemo(
    () => [
      {
        type: 'bias',
        score: Math.round(biasScore || 0),
        fill: 'hsl(var(--facts-pie-chart))',
      },
    ],
    [biasScore],
  );

  const biasEndAngle = useMemo(
    () => (biasChartData[0]?.score ? 90 + biasChartData[0]?.score * 3.6 : 90),
    [biasChartData],
  );

  return (
    <ChartWrapper title={biasLabel || 'Bias'} content={biasLabelReasoning}>
      <ChartContainer
        config={biasChartConfig}
        className="shrink-0 self-center aspect-square size-[54px] cred-md:size-[63px] cred-lg:size-[68px] cred-xl:size-[90px]"
      >
        <RadialBarChart
          data={biasChartData}
          startAngle={90}
          endAngle={biasEndAngle}
          innerRadius={chartDimensions.innerRadius}
          outerRadius={chartDimensions.outerRadius}
          margin={{
            top: -10,
            right: -10,
            bottom: -10,
            left: -10,
          }}
        >
          <PolarGrid
            gridType="circle"
            radialLines={false}
            stroke="none"
            className="transition-colors duration-200 first:fill-[#CDD9FF] last:fill-neutral-1"
            polarRadius={chartDimensions.polarRadius}
          />
          <RadialBar dataKey="score" background cornerRadius={10} />
          <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
            <Label
              content={({ viewBox }) => {
                if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={viewBox.cy || 0}
                        className="fill-black text-lg font-bold leading-5.25 cred-md:text-xl cred-md:leading-[23px] cred-lg:text-[22px] cred-lg:leading-[23px] cred-xl:text-[30px] cred-xl:leading-[35px]"
                      >
                        {biasChartData[0]?.score + '%'}
                      </tspan>
                    </text>
                  );
                }
              }}
            />
          </PolarRadiusAxis>
        </RadialBarChart>
      </ChartContainer>
    </ChartWrapper>
  );
};
export const ArticleCardBiasChart = memo(
  PureArticleCardBiasChart,
  (prevProps, nextProps) => {
    return (
      prevProps.biasScore === nextProps.biasScore &&
      prevProps.biasLabel === nextProps.biasLabel &&
      prevProps.biasLabelReasoning === nextProps.biasLabelReasoning
    );
  },
);
